/**
 * Poignée de fin d'un direct sur la grille. Miroir d'affichage de
 * `backend/src/lib/live-end-resize-plan.ts` : la poignée ne propose que ce que
 * le serveur acceptera, mais c'est lui qui tranche.
 *
 * Le direct prend sur l'infoservice/interlude qui le suit et s'arrête net sur
 * tout autre élément (pub, jingle, programme, direct) ; il dure au moins une
 * minute et, à l'antenne, sa fin reste à plus de 10 s de maintenant.
 */

export const LIVE_RESIZE_MIN_DURATION_MS = 60_000
export const LIVE_RESIZE_ON_AIR_LEAD_MS = 10_000
const GLUED_MS = 1_000

const RESIZABLE_STATUSES = new Set(['SCHEDULED', 'LOADING', 'ON_AIR'])
const SOFT_TYPES = new Set(['INFOSERVICE', 'INTERLUDE', 'FILLER'])

export interface LiveResizeItem {
  itemType:  string
  status:    string
}

/** La fin de ce direct se tire-t-elle sur la grille ? */
export function liveEndResizable(item: LiveResizeItem): boolean {
  return item.itemType === 'LIVE' && RESIZABLE_STATUSES.has(item.status)
}

export interface LiveResizeNeighbour {
  itemType: string
  status:   string
  startMs:  number
  endMs:    number
  /** Spot porteur d'un infoservice : sans lui, le serveur ne sait pas le redécouper. */
  infoserviceSourceId?: string | null
}

/**
 * Un élément qui cède sa place au direct : infoservice (porté par son spot)
 * ou interlude pas encore préchargé.
 */
function isSoft(item: LiveResizeNeighbour): boolean {
  if (!SOFT_TYPES.has(item.itemType) || item.status !== 'SCHEDULED') return false
  return item.itemType !== 'INFOSERVICE' || Boolean(item.infoserviceSourceId)
}

export function resolveLiveEndResize(input: {
  live:        { startMs: number; endMs: number; status: string }
  deltaMs:     number
  /** Éléments de la grille hors du direct lui-même et hors SKIPPED. */
  neighbours:  LiveResizeNeighbour[]
  nowMs:       number
  dayStartMs:  number
  dayEndMs:    number
  snapMinutes: number
  magnetMs:    number
}): number {
  const { live } = input
  const stepMs = Math.max(1_000, Math.round(input.snapMinutes * 60_000))
  const raw = live.endMs + input.deltaMs
  const snapped = input.dayStartMs + Math.round((raw - input.dayStartMs) / stepMs) * stepMs

  const after = input.neighbours
    .filter(item => item.startMs >= live.endMs - GLUED_MS)
    .sort((a, b) => a.startMs - b.startMs)
  const hardStart = after.find(item => !isSoft(item))?.startMs ?? null
  const upper = Math.min(input.dayEndMs, hardStart ?? input.dayEndMs)

  // Aimant : le bord le plus proche parmi ceux des éléments qui suivent (début
  // d'un dur, fin d'un souple). Jamais la fin actuelle du direct — le début de
  // l'élément collé derrière : à faible zoom l'aimant couvre plusieurs minutes
  // et ramenait la poignée à son point de départ (essai du 30/10/2026 : +3 min
  // tirées, aucune requête envoyée).
  const edges = after
    .flatMap(item => (isSoft(item) ? [item.startMs, item.endMs] : [item.startMs]))
    .filter(edge => Math.abs(edge - live.endMs) > GLUED_MS)
  const magnet = edges
    .filter(edge => Math.abs(edge - raw) <= input.magnetMs)
    .sort((a, b) => Math.abs(a - raw) - Math.abs(b - raw))[0]
  const boundary = magnet ?? snapped

  const lower = Math.max(
    live.startMs + LIVE_RESIZE_MIN_DURATION_MS,
    live.status === 'ON_AIR' ? input.nowMs + LIVE_RESIZE_ON_AIR_LEAD_MS : Number.NEGATIVE_INFINITY,
  )
  // Collé à un élément dur, le plafond est la fin actuelle : la poignée ne
  // propose pas d'allonger, seulement de raccourcir.
  return Math.min(Math.max(boundary, lower), Math.max(upper, live.endMs))
}
